import React from "react";
import { motion } from "framer-motion";
import { ArrowDown, Sparkles, Phone } from "lucide-react";
import Particles from "./Particles";
import { HERO } from "@/constants/testIds";
import { SITE, MEDIA } from "@/lib/site";

const wordVariants = {
  hidden: { opacity: 0, y: 24, filter: "blur(12px)" },
  show: (i) => ({
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { delay: 0.15 * i, duration: 0.9, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => {
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      id="hero"
      data-testid={HERO.root}
      className="relative min-h-[100svh] w-full overflow-hidden grain"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <img src={MEDIA.hero} alt="Spectacle de drones lumineux au-dessus d'un événement nocturne — drones3x" className="h-full w-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/60 to-[#030305]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(0,122,255,0.18),transparent_60%)]" />
      </div>

      <Particles count={55} />

      {/* Content */}
      <div className="relative z-10 mx-auto flex min-h-[100svh] max-w-7xl flex-col justify-center px-6 pt-32 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="inline-flex w-fit items-center gap-2 rounded-full glass px-4 py-2 text-xs uppercase tracking-[0.2em] text-white/70"
        >
          <Sparkles size={12} className="text-[#007AFF]" />
          Spectacles de drones premium · France · Europe · Maroc
        </motion.div>

        {/* SEO-optimised H1 (visually hidden for screen-readers & crawlers) */}
        <h1 className="sr-only">
          Spectacle de drones lumineux en France et en Europe — drones3x
        </h1>

        <h2
          data-testid={HERO.title}
          className="font-display mt-6 text-5xl md:text-7xl lg:text-[88px] font-light leading-[1.02] tracking-tight max-w-5xl"
        >
          {["Illuminez", "le", "ciel", "de"].map((w, i) => (
            <motion.span
              key={w + i}
              custom={i}
              initial="hidden"
              animate="show"
              variants={wordVariants}
              className="inline-block mr-3"
            >
              {w}
            </motion.span>
          ))}
          <motion.span
            custom={4}
            initial="hidden"
            animate="show"
            variants={wordVariants}
            className="inline-block font-semibold italic text-transparent bg-clip-text bg-gradient-to-r from-white via-[#7fb6ff] to-[#007AFF]"
          >
            vos émotions.
          </motion.span>
        </h2>

        <motion.p
          data-testid={HERO.subtitle}
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.7 }}
          className="font-body mt-8 max-w-2xl text-base md:text-lg text-white/70 leading-relaxed"
        >
          Entreprise Française de spectacle de drones aériens — France, Europe et Maroc.
          Nous orchestrons jusqu'à 800 drones lumineux synchronisés pour transformer
          vos mariages, événements corporate, festivals et cérémonies sportives en
          moments inoubliables.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.7 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid={HERO.ctaPricing}
            onClick={() => scrollTo("pricing")}
            className="group inline-flex items-center gap-3 rounded-full bg-[#007AFF] px-7 py-4 text-sm font-semibold text-white hover:shadow-[0_0_36px_rgba(0,122,255,0.6)] transition-all"
          >
            Voir nos tarifs
            <span className="inline-block transition-transform group-hover:translate-x-1">→</span>
          </button>
          <a
            data-testid={HERO.ctaContact}
            href={SITE.phoneLink}
            className="inline-flex items-center gap-3 rounded-full glass px-7 py-4 text-sm font-medium text-white hover:bg-white/10 transition-all"
          >
            <Phone size={16} /> {SITE.phoneDisplay}
          </a>
        </motion.div>

        {/* Floating mini stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.8 }}
          className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-px overflow-hidden rounded-2xl glass-strong"
        >
          {[
            { v: "80+", l: "Spectacles" },
            { v: "50K+", l: "Spectateurs" },
            { v: "100%", l: "Satisfaction" },
            { v: "24/7", l: "Studio créatif" },
          ].map((s) => (
            <div key={s.l} className="bg-black/30 p-5">
              <div className="font-display text-2xl md:text-3xl font-semibold">{s.v}</div>
              <div className="text-xs uppercase tracking-[0.18em] text-white/50 mt-1">{s.l}</div>
            </div>
          ))}
        </motion.div>

        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/40 animate-bounce">
          <ArrowDown size={18} />
        </div>
      </div>
    </section>
  );
};

export default Hero;
